import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import TrendingUp from "lucide-react/dist/esm/icons/trending-up";
import Target from "lucide-react/dist/esm/icons/target";
import Zap from "lucide-react/dist/esm/icons/zap";
import Clock from "lucide-react/dist/esm/icons/clock";
import Award from "lucide-react/dist/esm/icons/award";
import Users from "lucide-react/dist/esm/icons/users";
import BookOpen from "lucide-react/dist/esm/icons/book-open";
import Calendar from "lucide-react/dist/esm/icons/calendar";
import Mail from "lucide-react/dist/esm/icons/mail";
import Search from "lucide-react/dist/esm/icons/search";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useEmailSubscription } from "@/hooks/useEmailSubscription";
import { useTips } from "@/hooks/useTips";
import { getLatestTipPath } from "@/lib/navigation";
import Navigation from "@/components/Navigation";
import { ShareButton } from "@/components/ShareButton";
import { InteractiveRating } from "@/components/InteractiveRating";
import { PinButton } from "@/components/PinButton";
import { SearchBar } from "@/components/SearchBar";
import { TipContent } from "@/components/TipContent";
import { YouTubePlayer } from "@/components/YouTubePlayer";
import { SEO } from "@/components/SEO";
import { StructuredData } from "@/components/StructuredData";
import { useSEO } from "@/hooks/useSEO";

// Helper function to get unique image based on category and ID
const getUniqueImage = (category?: string, id?: string) => {
  const categoryLower = category?.toLowerCase() || '';
  
  // Create a more robust hash from the ID using multiple characteristics
  let hash = 0;
  if (id) {
    // Use the full ID string for hashing
    for (let i = 0; i < id.length; i++) {
      const char = id.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    // Add additional entropy using ID parts
    const idParts = id.split('-');
    for (const part of idParts) {
      for (let i = 0; i < part.length; i++) {
        hash = ((hash << 3) - hash) + part.charCodeAt(i);
      }
    }
  }
  
  // Use a larger pool of images regardless of category to ensure uniqueness
  const allImages = [
    'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1523712999610-f77fbcfc3843?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1470813740244-df37b8c1edcb?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1545389336-cf090694435e?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1517180102446-f3ece451e9d8?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1542626991-cbc4e32524cc?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1434494878577-86c23bcb06b9?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1500375592092-40eb2168fd21?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1495592822108-9e6261896da8?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1519834785169-98be25ec3f84?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1482881497185-d4a9ddbe4151?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1433086966358-54859d0ed716?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1472396961693-142e6e269027?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1506126613408-eca07ce68e71?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1482938289607-e9573fc25ebb?w=400&h=300&fit=crop'
  ];
  
  const imageIndex = Math.abs(hash) % allImages.length;
  return allImages[imageIndex];
};
import { TodaysTechTip } from "@/components/TodaysTechTip";
import { TodaysInvestmentTip } from "@/components/TodaysInvestmentTip";
import heroImage from "@/assets/hero-image.jpg";

const Index = () => {
  const [email, setEmail] = useState("");
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const { subscribeToEmail, isLoading } = useEmailSubscription();
  
  // Defer tips loading to break critical request chain - load after initial render
  const { tips } = useTips({ 
    enabled: isPageLoaded, // Only load after page is ready
  });
  
  // Defer page loaded state to break dependency chain
  useEffect(() => {
    // Use requestIdleCallback to defer until browser is idle
    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => setIsPageLoaded(true));
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => setIsPageLoaded(true), 100);
    }
  }, []);

  // Get the latest published tip with fallback for loading state
  const publishedTips = tips?.filter(tip => tip.is_published)
    .sort((a, b) => new Date(b.published_at || b.created_at).getTime() - new Date(a.published_at || a.created_at).getTime()) || [];
  
  const latestTip = publishedTips[0];
  
  // Get tips sorted chronologically for numbering
  const chronologicalTips = tips?.filter(tip => tip.is_published)
    .sort((a, b) => new Date(a.published_at || a.created_at).getTime() - new Date(b.published_at || b.created_at).getTime()) || [];
  
  // Find tip number based on chronological order
  const tipNumber = latestTip ? chronologicalTips.findIndex(t => t.id === latestTip.id) + 1 : 0;

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      const result = await subscribeToEmail(email);
      if (result.success) {
        setEmail("");
      }
    }
  };

  // Apply SEO optimization
  useSEO({
    title: "1% A Day - Daily Self-Improvement Tips",
    description: "Transform your life with daily practical tips for continuous improvement. Small changes, compound results. Get 1% better each day and you'll be 37x better by year's end.",
    canonicalPath: "/"
  });

  return (
    <>
      <SEO
        title="1% A Day - Daily Self-Improvement Tips"
        description="Transform your life with daily practical tips for continuous improvement. Small changes, compound results. Get 1% better each day and you'll be 37x better by year's end."
        url={window.location.origin}
        type="website"
        image="https://lovable.dev/opengraph-image-p98pqg.png"
      />
      <StructuredData type="homepage" />
      <div className="min-h-screen bg-background text-foreground">
        <Navigation />
      {/* Hero Section - Optimized for LCP */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-secondary">
        {/* Hero image optimized for LCP - largest contentful paint element */}
        <img 
          src={heroImage}
          alt="Hero background showing self-improvement concept"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
          fetchPriority="high"
          decoding="sync"
          loading="eager"
          width="1335"
          height="940"
          style={{ 
            contentVisibility: 'visible',
            containIntrinsicSize: '1335px 940px'
          }}
        />
        <div className="absolute inset-0 bg-gradient-secondary" />
        
        <div className="relative z-10 text-center max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 animate-fade-in">
          <Badge className="mb-4 sm:mb-6 bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 transition-all duration-300">
            <TrendingUp className="mr-2 h-4 w-4" />
            Daily Self-Improvement
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold mb-4 sm:mb-6 bg-gradient-primary bg-clip-text text-transparent animate-glow">
            1% A Day
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground mb-6 px-2 sm:px-0 animate-fade-in-up">
            Transform your life with daily practical tips for continuous improvement. 
            Small changes, compound results.
          </p>
          
          <div className="bg-gradient-secondary rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 mx-2 sm:mx-0 animate-scale-in">
            <p className="text-base sm:text-lg md:text-xl text-center">
              <span className="text-muted-foreground block sm:inline">Get 1% better each day and you'll be</span>
              <span className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent mx-2 block sm:inline">37x better</span>
              <span className="text-muted-foreground block sm:inline">by year's end</span>
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center animate-scale-in px-2 sm:px-0">
            <Button asChild variant="hero" size="lg" className="group w-full sm:w-auto">
              <Link to="/archive">
                Start Your Journey
                <Target className="ml-2 h-5 w-5 group-hover:scale-110 transition-transform" />
              </Link>
            </Button>
            <Button asChild variant="glow" size="lg" className="w-full sm:w-auto">
              <Link to={getLatestTipPath(tips)}>
                <BookOpen className="mr-2 h-5 w-5" />
                Read Today's Tip
              </Link>
            </Button>
          </div>
        </div>
        
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-float">
          <div className="w-6 h-10 border-2 border-primary/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-primary rounded-full mt-2 animate-pulse" />
          </div>
        </div>
      </section>

      {/* The 1% Concept Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 animate-fade-in">
              The Power of <span className="bg-gradient-primary bg-clip-text text-transparent">1%</span>
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto px-2 sm:px-0">
              If you get 1% better each day for one year, you'll end up 37 times better. 
              That's the magic of compound improvement.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
            {[
              {
                icon: <Clock className="h-8 w-8" />,
                title: "Daily Consistency",
                description: "Small, manageable improvements that fit into your routine",
                link: "/archive"
              },
              {
                icon: <Zap className="h-8 w-8" />,
                title: "Compound Growth",
                description: "Tiny gains accumulate into remarkable transformations",
                link: "/about"
              },
              {
                icon: <Award className="h-8 w-8" />,
                title: "Proven Results",
                description: "Based on scientific research and real-world success stories",
                link: "/experiences"
              }
            ].map((item, index) => (
              <Card key={index} className="group hover:shadow-glow-primary transition-all duration-300 hover:scale-105 bg-card/50 backdrop-blur-sm border-border/50">
                <Link to={item.link} className="block h-full">
                  <CardHeader className="text-center">
                    <div className="flex flex-col items-center">
                      <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-primary/30 border-2 border-primary/40 mb-4 group-hover:animate-float group-hover:shadow-glow-primary">
                        <div className="text-primary drop-shadow-sm">
                          {item.icon}
                        </div>
                      </div>
                      <CardTitle className="text-xl group-hover:text-primary transition-colors">{item.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-center text-muted-foreground group-hover:text-foreground transition-colors">
                      {item.description}
                    </CardDescription>
                  </CardContent>
                </Link>
              </Card>
            ))}
          </div>
          
          {/* Progress Visualization */}
          <div className="bg-gradient-secondary rounded-2xl p-4 sm:p-6 lg:p-8 text-center">
            <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Your Improvement Journey</h3>
            <div className="flex items-center justify-between max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-muted-foreground">Day 1</div>
                <div className="text-xs sm:text-sm text-muted-foreground">Starting Point</div>
              </div>
              <div className="flex-1 mx-4 sm:mx-8">
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-primary animate-pulse" style={{ width: '100%' }} />
                </div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary">Day 365</div>
                <div className="text-xs sm:text-sm text-primary">37x Better</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Tip Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-secondary">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 sm:mb-6 bg-primary/10 text-primary border-primary/20">
            <Calendar className="mr-2 h-4 w-4" />
            Today's Tip
          </Badge>
          
          {latestTip ? (
            <Card className="bg-card/50 backdrop-blur-sm border-border/50 hover:shadow-glow-primary transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-xl sm:text-2xl md:text-3xl px-2 sm:px-0">
                  {latestTip.title}
                </CardTitle>
                <CardDescription className="text-base sm:text-lg text-muted-foreground px-2 sm:px-0">
                  Tip #{tipNumber} • {latestTip.category || 'General'}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-left">
                {/* Tip Image - Lazy load for better FCP */}
                <div className="mb-6">
                  <img
                    src={latestTip.image_url || getUniqueImage(latestTip.category, latestTip.id)}
                    alt={`${latestTip.title} - Daily self-improvement tip about ${latestTip.category || 'personal development'}`}
                    className="w-full h-48 md:h-64 object-cover rounded-lg border border-border/20"
                    loading="lazy"
                    decoding="async"
                    width="600"
                    height="400"
                    style={{ 
                      contentVisibility: 'auto',
                      containIntrinsicSize: '600px 400px'
                    }}
                    onError={(e) => {
                      console.error('Tip image failed to load, using fallback');
                      const target = e.target as HTMLImageElement;
                      target.src = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop';
                    }}
                  />
                </div>
                
                <TipContent 
                  content={latestTip.content}
                  className="mb-6 text-lg"
                />
                
                {/* Share and Rate Section */}
                <div className="space-y-6 mb-6">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      <ShareButton
                        title={latestTip.title}
                        content={latestTip.content}
                        url={`${window.location.origin}/tips/${latestTip.id}`}
                        size="sm"
                        variant="outline"
                      />
                      <PinButton 
                        tipId={latestTip.id}
                        size="default"
                        variant="outline"
                      />
                    </div>
                    <Button asChild variant="gradient" className="w-full sm:w-auto">
                      <Link to={getLatestTipPath(tips)}>Try This Tip Now</Link>
                    </Button>
                  </div>
                
                {/* YouTube Video */}
                {latestTip.youtube_url && (
                  <div className="mb-6">
                    <YouTubePlayer 
                      videoUrl={latestTip.youtube_url}
                      title={`Video: ${latestTip.title}`}
                    />
                  </div>
                )}
                  <div className="border-t border-border/20 pt-6">
                    <InteractiveRating
                      tipId={latestTip.id}
                      currentRating={latestTip.rating || 5.0}
                      ratingCount={latestTip.rating_count || 0}
                      title={latestTip.title}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-card/50 backdrop-blur-sm border-border/50">
              <CardHeader>
                <CardTitle className="text-2xl md:text-3xl">
                  Loading Today's Tip...
                </CardTitle>
              </CardHeader>
            </Card>
          )}
        </div>
      </section>

      {/* Tech Tip Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <TodaysTechTip />
        </div>
      </section>

      {/* Investment Tip Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-secondary">
        <div className="max-w-4xl mx-auto">
          <TodaysInvestmentTip />
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 text-center">
            {[
              {
                number: "10,000+",
                label: "Daily Readers",
                icon: <Users className="h-6 w-6" />
              },
              {
                number: `${publishedTips.length}+`,
                label: "Practical Tips",
                icon: <BookOpen className="h-6 w-6" />
              },
              {
                number: "95%",
                label: "Report Improvement",
                icon: <TrendingUp className="h-6 w-6" />
              }
            ].map((stat, index) => {
              // Make the "Practical Tips" stat clickable to go to archive
              if (index === 1) { // "Practical Tips" is at index 1
                return (
                  <Link key={index} to="/archive" className="group hover:scale-105 transition-all duration-300 block">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-primary mb-4 text-primary-foreground group-hover:animate-float">
                      {stat.icon}
                    </div>
                    <div className="text-4xl font-bold mb-2 bg-gradient-primary bg-clip-text text-transparent">
                      {stat.number}
                    </div>
                    <div className="text-muted-foreground group-hover:text-primary transition-colors">
                      {stat.label}
                    </div>
                  </Link>
                );
              }
              
              return (
                <div key={index} className="group hover:scale-105 transition-all duration-300">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-primary mb-4 text-primary-foreground group-hover:animate-float">
                    {stat.icon}
                  </div>
                  <div className="text-4xl font-bold mb-2 bg-gradient-primary bg-clip-text text-transparent">
                    {stat.number}
                  </div>
                  <div className="text-muted-foreground">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-secondary">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 sm:mb-6 bg-primary/10 text-primary border-primary/20">
            <Search className="mr-2 h-4 w-4" />
            Find Your Perfect Tip
          </Badge>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-foreground">
            Search Our Tip Library
          </h2>
          
          <p className="text-lg sm:text-xl text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto px-2 sm:px-0">
            Looking for something specific? Search through our entire collection of practical tips 
            to find exactly what you need for your improvement journey.
          </p>
          
          <div className="max-w-2xl mx-auto mb-6 sm:mb-8 px-2 sm:px-0">
            <SearchBar 
              placeholder="Search tips by keyword, category, or topic..."
              className="w-full"
            />
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-2 sm:px-0">
            <Button asChild variant="outline" size="lg" className="bg-background/50 hover:bg-background/70 w-full sm:w-auto">
              <Link to="/search">Advanced Search</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="bg-background/50 hover:bg-background/70 w-full sm:w-auto">
              <Link to="/archive">Browse All Tips</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-primary-foreground">
            Get Daily Tips in Your Inbox
          </h2>
          <p className="text-lg sm:text-xl text-primary-foreground/90 mb-6 sm:mb-8 max-w-2xl mx-auto px-2 sm:px-0">
            Join thousands who are already transforming their lives one small step at a time. 
            Receive your daily dose of 1% improvement every morning.
          </p>
          
          <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto mb-6 sm:mb-8 px-2 sm:px-0">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/60 focus:border-primary-foreground/50 h-12"
                />
              </div>
              <Button 
                type="submit" 
                disabled={isLoading}
                variant="secondary" 
                size="lg" 
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 w-full sm:w-auto"
              >
                <Mail className="mr-2 h-5 w-5" />
                {isLoading ? "Subscribing..." : "Subscribe"}
              </Button>
            </div>
          </form>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-2 sm:px-0">
            <Button asChild variant="outline" size="lg" className="border-primary-foreground/40 text-primary-foreground bg-primary-foreground/10 hover:bg-primary-foreground/20 w-full sm:w-auto">
              <Link to={getLatestTipPath(tips)}>Read Today's Tip</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-primary-foreground/40 text-primary-foreground bg-primary-foreground/10 hover:bg-primary-foreground/20 w-full sm:w-auto">
              <Link to="/about">About Our Mission</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-background border-t border-border/20">
        <div className="max-w-6xl mx-auto text-center">
          <div className="text-2xl font-bold mb-4 bg-gradient-primary bg-clip-text text-transparent">
            1% A Day
          </div>
          <p className="text-muted-foreground mb-6">
            Small changes. Big results. Every single day.
          </p>
          <div className="flex justify-center space-x-6 text-muted-foreground">
            <Link to="/about" className="hover:text-primary transition-colors">About</Link>
            <Link to="/archive" className="hover:text-primary transition-colors">Archive</Link>
            <Link to="/admin" className="hover:text-primary transition-colors">Admin</Link>
            <Link to="/contact" className="hover:text-primary transition-colors">Contact</Link>
            <a href="https://aichuckles.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Daily Smiles</a>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default Index;
